




























































































































import '../../styles/upsert-proposal-modal.css'
import Vue from 'vue'
import { CIcon } from '@coreui/icons-vue'
import { cilCamera, cilX, cilFolder } from '@coreui/icons-pro'

import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

import Modal from '../general/modal/Modal.vue'
import ModalBody from '../general/modal/ModalBody.vue'
import SmallDisplay from '../general/SmallDisplay.vue'
import Folder from '../general/Folder.vue'
import { IValidationResult } from '../../util/IValidationResult'

enum IDocumentSource {
  MDResource = 'MDResource',
  AgentResource = 'AgentResource',
  Report = 'Report',
  Other = 'Other',
}

interface IDocument {
  id: number
  name?: string
  link?: string | undefined
  imageUrl?: string | undefined
  source: IDocumentSource
  documents?: IDocument[]
}

interface IProposal {
  id: number
  title: string
  imageUrl: string | undefined
  documents?: IDocument[]
  numberOfChildren?: number
}

interface IMDResource {
  id: number
  name: string
  parentId: number
  isDirectory: boolean
  imageUrl: string | undefined
  resourceLink: string | undefined
}

interface IAgentResource {
  id: number
  name: string
  description: string
  imageUrl: string | undefined
  resourceLink: string | undefined
}

export default Vue.extend({
  mixins: [validationMixin],
  validations: {
    title: { required },
  },
  data() {
    return {
      cameraIcon: cilCamera,
      exitIcon: cilX,
      folderIcon: cilFolder,
      title: '',
      imageData: '',

      tab: 'mdResources',
      tabs: ['mdResources', 'agentResources', 'reports'],
      errors: [],

      loading: true,
      saving: false,
      documents: {
        mdResources: [] as IDocument[],
        agentResources: [] as IDocument[],
        reports: [] as IDocument[],
      },
      selectedDocuments: [] as IDocument[],
    }
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    proposal: {
      type: Object,
      required: false,
      default: null,
    },
  },
  watch: {
    proposal: function (val) {
      if (val.id) {
        this.title = val.title
        this.selectedDocuments = val.selectedDocuments
      } else {
        this.title = ''
        this.selectedDocuments = []
      }
    },
  },
  mounted: async function () {
    await this.populateMDResources()
    await this.populateAgentResources()
    this.loading = false
  },
  updated: function () {
    if (this.$refs.fileinput) {
      ;(this.$refs.fileinput as HTMLInputElement).onchange = () => {
        const files = (this.$refs.fileinput as HTMLInputElement).files

        if (files && files.length > 0) {
          const file = files[0]
          if (file) {
            this.imageData = URL.createObjectURL(file)
          }
        }
      }
    }
  },
  methods: {
    populateMDResources: async function () {
      const pullDocuments = async (id?: number): Promise<IDocument[]> => {
        const compiledDocuments: IDocument[] = []
        const resources: IMDResource[] = await (
          await fetch(`/api/mdresources${id ? `?parentId=${id}` : ''}`)
        ).json()

        for (const resource of resources) {
          const document: IDocument = {
            id: resource.id,
            name: resource.name,
            imageUrl: resource.imageUrl,
            link: resource.resourceLink,
            source: IDocumentSource.MDResource,
          }

          if (resource.isDirectory)
            document.documents = await pullDocuments(resource.id)

          compiledDocuments.push(document)
        }

        return compiledDocuments
      }

      this.documents.mdResources = await pullDocuments()
    },
    populateAgentResources: async function () {
      const compiledDocuments: IDocument[] = []
      const resources: IAgentResource[] = await (
        await fetch(`/api/agentresources`)
      ).json()

      for (const resource of resources) {
        const document: IDocument = {
          id: resource.id,
          name: resource.name,
          imageUrl: resource.imageUrl,
          link: resource.resourceLink,
          source: IDocumentSource.AgentResource,
        }

        compiledDocuments.push(document)
      }

      this.documents.agentResources = compiledDocuments
    },
    selectDocument: function (document: IDocument) {
      const existingDocument = this.selectedDocuments.find(
        (value) => value.id === document.id && value.source === document.source
      )

      if (existingDocument) {
        this.selectedDocuments = this.selectedDocuments.filter(
          (sd) => sd.id !== document.id || sd.source !== document.source
        )
      } else {
        this.selectedDocuments.push(document)
      }
    },
    submit: async function () {
      // TODO: Show error message if the selected documents are emtpy
      if (this.$v.title.$invalid || !this.selectDocument.length) {
        this.$v.$touch()
        return
      }

      this.saving = true

      const result = await fetch(
        `/api/proposals${this.editMode ? `/${this.proposal.id}` : ''}`,
        {
          method: this.editMode ? 'PUT' : 'POST',
          body: JSON.stringify({
            title: this.title,
            documents: this.selectedDocuments,
          }),
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
          },
        }
      )

      if (!result.ok) {
        this.errors = (await result.json()).map(
          (x: IValidationResult) => x.errorMessage
        )
      } else {
        const body = await result.json()
        const files = (this.$refs.fileinput as HTMLInputElement).files
        if (files && files.length > 0) {
          const file = files[0]
          if (file) {
            const uploadImageUrl = body.uploadImageUrl
            await fetch(uploadImageUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': 'image/png',
                'x-ms-blob-type': 'BlockBlob',
              },
            })
          }
        }

        this.close({ ...body, numberOfChildren: this.selectedDocuments.length })
      }

      this.saving = false
    },
    selectFile: async function (): Promise<void> {
      ;(this.$refs.fileinput as HTMLInputElement).click()
    },
    close: function (data: IProposal | undefined) {
      this.title = ''
      this.imageData = ''
      this.selectedDocuments = []

      this.$v.$reset()
      this.$emit('close', data)
    },
  },
  computed: {
    tabDocuments() {
      const vueInstance = this as any
      let activeDocuments: IDocument[] = []

      if (vueInstance.documents[vueInstance.tab].length)
        activeDocuments = vueInstance.documents[vueInstance.tab]

      return activeDocuments
    },
    titleErrors() {
      const vueInstance = this as any
      if (!vueInstance.titleModel) {
        return ['Title is required']
      }
      return []
    },
    titleModel: {
      get() {
        const vueInstance = this as any

        if (vueInstance.editMode) {
          return vueInstance.proposal.title || vueInstance.title
        }

        return vueInstance.title
      },
      set(value) {
        ;(this as any).title = value
      },
    },
  },
  components: { CIcon, Modal, ModalBody, SmallDisplay, Folder },
  name: 'UpsertProposalModal',
})
